import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authAxios } from "../utils/auth";
import { useCoinGate } from "../contexts/CoinGateContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import AdminMenu from "../components/AdminMenu";
import MainMenu from "../components/MainMenu";
import CoinBalancePill from "../components/CoinBalancePill";
import NotificationBell from "../components/notifications/NotificationBell";
import {
  ArrowLeft, Plus, Heart, Users, Home,
  Search, Instagram, Twitter, Facebook, Edit, MessageSquare,
  CheckCircle2, Bell, BellOff,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import AddPersonaModal from "../components/AddPersonaModal";
import UpgradeRequiredModal from "../components/UpgradeRequiredModal";

const MatchesGallery = () => {
  const navigate = useNavigate();
  const { requireCoins } = useCoinGate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  // Upgrade modal is opened when the "New Relationship" button is clicked
  // but the user has hit their subscription-tier relationship cap.
  const [upgradeState, setUpgradeState] = useState(null);
  // Tracks the match currently being toggled so we can disable its bell
  // briefly and prevent rapid double-taps from racing the PATCH.
  const [togglingMatchId, setTogglingMatchId] = useState(null);
  // The match whose AI Roleplay modal is currently open. The Roleplay flow
  // is still in beta and is intentionally walled off from this gallery to
  // avoid users tripping into the unfinished /chat/{id} screen.
  const [roleplayModalMatch, setRoleplayModalMatch] = useState(null);

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      const response = await authAxios.get("/matches");
      setMatches(response.data);
    } catch (error) {
      console.error("Error fetching matches:", error);
    } finally {
      setLoading(false);
    }
  };

  // Gate the "New Relationship" button on the subscription-tier cap. If the
  // user is already at their cap, open the upgrade modal instead of the
  // create-a-persona modal. Also gate on coin balance — an empty wallet
  // opens the global "Out of Coins" modal via CoinGateProvider.
  const handleAddClick = async () => {
    if (!(await requireCoins())) return;
    try {
      const res = await authAxios.get("/matches/quota");
      if (res.data?.can_create) {
        setShowAddModal(true);
      } else {
        setUpgradeState({
          tier: res.data?.tier || "FREE",
          used: res.data?.used || 0,
          limit: res.data?.limit ?? 0,
        });
      }
    } catch (err) {
      // Fail open — if the quota check itself errors we still let the user
      // try to create, and the backend will 402 with the same detail block.
      console.error("Failed to check relationship quota:", err);
      setShowAddModal(true);
    }
  };

  const handleEditMatch = (match) => {
    navigate(`/relationships/${match.match_id}/edit`);
  };

  // Toggle the per-match "Display reminders on Dashboard" flag. We treat
  // missing/undefined as ON so existing relationships keep prior behaviour.
  const handleToggleReminders = async (match, e) => {
    e?.stopPropagation?.();
    if (togglingMatchId) return;

    const wasOn = match.reminders_enabled !== false;
    const nextOn = !wasOn;
    const firstName = (match.name || "").split(" ")[0] || match.name || "";

    // Optimistic local update — Dashboard re-reads on next visit.
    setMatches((prev) =>
      prev.map((m) =>
        m.match_id === match.match_id ? { ...m, reminders_enabled: nextOn } : m
      )
    );
    setTogglingMatchId(match.match_id);

    try {
      await authAxios.patch(`/matches/${match.match_id}`, {
        reminders_enabled: nextOn,
      });
      toast.success(
        `Event Reminders List for ${firstName} is now ${nextOn ? "ON" : "OFF"}`
      );
    } catch (error) {
      // Roll back on failure.
      setMatches((prev) =>
        prev.map((m) =>
          m.match_id === match.match_id ? { ...m, reminders_enabled: wasOn } : m
        )
      );
      toast.error(
        error?.response?.data?.detail || "Failed to update reminders setting"
      );
    } finally {
      setTogglingMatchId(null);
    }
  };

  const filteredMatches = matches
    .filter(match => match.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      // user_persona always at top
      if (a.match_type === "user_persona") return -1;
      if (b.match_type === "user_persona") return 1;
      return 0;
    });

  const getPlatformIcon = (platform) => {
    const icons = {
      instagram: <Instagram className="w-4 h-4" />,
      twitter: <Twitter className="w-4 h-4" />,
      facebook: <Facebook className="w-4 h-4" />,
    };
    return icons[platform] || null;
  };

  return (
    <div className="min-h-screen bg-divine-black">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="border-b border-white/10 bg-divine-black-light sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="icon"
              onClick={() => navigate("/dashboard")}
              className="text-white/60 hover:text-white"
              title="Home"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png"
                alt="Agent Eros"
                className="w-6 h-6 object-contain"
                style={{ animation: "eros-glow-gold 3s ease-in-out infinite" }}
              />
            </Button>
            <div>
              <h1 className="font-cinzel text-lg text-white">Relationships</h1>
            </div>
          </div>
          
          {/* Desktop only - New Relationship in header */}
          <div className="flex items-center gap-2">
            <Button
              data-testid="add-match-btn-header"
              onClick={handleAddClick}
              className="hidden md:flex bg-divine-red hover:bg-divine-red-light text-white font-cinzel rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Relationship
            </Button>
            <CoinBalancePill />
            <NotificationBell />
            <AdminMenu />
            <MainMenu exclude="/relationships" />
          </div>
        </div>
      </header>

      {/* Mobile New Relationship + Search Bar */}
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-4">
        {/* Mobile only - New Relationship above search */}
        <Button
          data-testid="add-match-btn-mobile"
          onClick={handleAddClick}
          className="md:hidden w-full bg-divine-red hover:bg-divine-red-light text-white font-cinzel rounded-full"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Relationship
        </Button>
        
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <Input
            data-testid="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search your relationships..."
            className="pl-12 bg-white/5 border-white/10 text-white placeholder:text-white/30"
          />
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 pb-12">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin w-8 h-8 border-2 border-divine-gold border-t-transparent rounded-full" />
          </div>
        ) : filteredMatches.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMatches.map((match) => {
              const isUserPersona = match.match_type === "user_persona";
              return (
              <div
                key={match.match_id}
                data-testid={`match-card-${match.match_id}`}
                onClick={() => navigate(`/relationships/${match.match_id}`)}
                className={`relative rounded-xl p-6 cursor-pointer card-hover group ${
                  isUserPersona 
                    ? "bg-gradient-to-br from-divine-gold/20 to-divine-purple/20 border-2 border-divine-gold/50 ring-2 ring-divine-gold/20" 
                    : "glass-gold"
                }`}
              >
                {/* User Profile Badge */}
                {isUserPersona && (
                  <div className="absolute -top-2 -right-2 bg-divine-gold text-black text-xs font-bold px-2 py-1 rounded-full">
                    MY PROFILE
                  </div>
                )}
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center overflow-hidden ${
                    isUserPersona ? "bg-divine-gold/30 ring-2 ring-divine-gold" : "bg-divine-gold/20"
                  }`}>
                    {(match.thumbnail_url || match.picture_url || match.picture) ? (
                      <img 
                        src={match.thumbnail_url || match.picture_url || match.picture} 
                        alt={match.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className={`font-cinzel text-2xl ${isUserPersona ? "text-divine-gold" : "text-divine-gold"}`}>
                        {match.name[0]}
                      </span>
                    )}
                  </div>
                  {!isUserPersona && match.compatibility_score && (
                    <div className="text-right">
                      <p className="text-divine-gold font-cinzel text-2xl">{match.compatibility_score}%</p>
                      <p className="text-white/40 text-xs">Match</p>
                    </div>
                  )}
                </div>
                
                <h3 className={`font-cinzel text-xl mb-2 ${isUserPersona ? "text-cyan-400" : "text-white"}`}>
                  {match.name}
                </h3>
                
                {/* Hide relationship_status pill on the user's own MY PROFILE
                    card — the field is meaningless there (a user is not in a
                    relationship with themselves). */}
                {match.relationship_status && !isUserPersona && (
                  <div className="flex items-center gap-2 text-white/40 text-sm mb-3">
                    <Heart className="w-4 h-4" />
                    <span className="capitalize">{match.relationship_status.replace(/_/g, " ")}</span>
                  </div>
                )}
                
                {match.notes && (
                  <p className="text-white/60 text-sm line-clamp-2 mb-4">{match.notes}</p>
                )}
                
                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <span className="text-white/40 text-xs flex items-center gap-1">
                    {match.profile_completion === 100 ? (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
                        <span className="text-green-400">Profile Complete</span>
                      </>
                    ) : (
                      `Profile ${match.profile_completion || 0}% Complete`
                    )}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/eroscoach/${match.match_id}`);
                      }}
                      className="text-divine-gold hover:bg-divine-gold/10 h-8 px-2"
                      title="AgentEros Advice"
                    >
                      <span className="text-sm">🏹</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        // AI Roleplay is still in beta — gate access behind
                        // the same "Beta Development" modal shown on the
                        // relationship detail page so users don't slip into
                        // the unfinished /chat/{match_id} flow from here.
                        setRoleplayModalMatch(match);
                      }}
                      className="text-white/30 hover:text-white/60 hover:bg-white/5 h-8 px-2"
                      title="AI Roleplay (beta — coming soon)"
                      data-testid={`roleplay-beta-trigger-${match.match_id}`}
                    >
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditMatch(match);
                      }}
                      className="text-white/60 hover:bg-white/10 h-8 px-2"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {!isUserPersona && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => handleToggleReminders(match, e)}
                        disabled={togglingMatchId === match.match_id}
                        className={`h-8 px-2 ${
                          match.reminders_enabled !== false
                            ? "text-divine-gold hover:bg-divine-gold/10"
                            : "text-white/30 hover:bg-white/5"
                        }`}
                        title={
                          match.reminders_enabled !== false
                            ? `Dashboard reminders ON — click to mute ${match.name}`
                            : `Dashboard reminders OFF — click to enable for ${match.name}`
                        }
                        aria-pressed={match.reminders_enabled !== false}
                        data-testid={`reminders-toggle-${match.match_id}`}
                      >
                        {match.reminders_enabled !== false ? (
                          <Bell className="w-4 h-4" />
                        ) : (
                          <BellOff className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-16 glass rounded-xl">
            <Users className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="font-cinzel text-xl text-white mb-2">
              {searchTerm ? "No relationships found" : "Your Constellation Awaits"}
            </h3>
            <p className="text-white/40 mb-6">
              {searchTerm 
                ? "Try a different search term" 
                : "Add potential relationships to begin your divine journey"
              }
            </p>
            {!searchTerm && (
              <Button
                onClick={() => setShowAddModal(true)}
                className="bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Persona
              </Button>
            )}
          </div>
        )}
      </main>

      {/* Add Persona Modal */}
      <AddPersonaModal 
        isOpen={showAddModal} 
        onClose={() => setShowAddModal(false)}
        existingMatches={matches}
      />

      {/* Upgrade-required modal — opened when the user hits their
          subscription-tier relationship cap. */}
      <UpgradeRequiredModal
        state={upgradeState}
        onClose={() => setUpgradeState(null)}
      />

      {/* Roleplay Beta Modal — mirrors the copy on /relationships/:matchId so
          gating is consistent across entry points. */}
      {roleplayModalMatch && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => setRoleplayModalMatch(null)}
          data-testid="roleplay-beta-modal"
        >
          <div
            className="bg-divine-black-light border border-white/10 rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-divine-gold/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <MessageSquare className="w-6 h-6 text-divine-gold" />
              </div>
              <h3 className="font-cinzel text-lg text-divine-gold">Beta Development</h3>
            </div>
            <p className="text-white/70 text-sm leading-relaxed mb-2">
              We are currently working on this Beta feature to be released soon.
            </p>
            <p className="text-white/70 text-sm leading-relaxed mb-4">
              For a lite Roleplay experience, in any coaching session you can ask Agent Eros to roleplay a response, enter full roleplay and chat. The dedicated AI Roleplay mode will be more advanced for realistic but simulated persona interactions.
            </p>
            <Button
              data-testid="roleplay-beta-close"
              onClick={() => setRoleplayModalMatch(null)}
              variant="outline"
              className="w-full border-white/20 text-white/60 hover:text-white hover:bg-white/5 rounded-full"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchesGallery;
